// Image generator: user-supplied image as a layer. Image data is too large
// to fit in the DSL/URL, so it lives in a separate cache (in-memory + an
// optional localStorage data URL persistence). Layers reference it by id.

import type { LayerSpec } from '@/core/pipeline';

type ImageParams = {
  src: string;       // image id (not data); 'none' if absent
  fit: 'cover' | 'contain' | 'stretch';
};

const imageCache = new Map<string, ImageData>();

export function setImage(id: string, data: ImageData): void {
  imageCache.set(id, data);
}

export function getImage(id: string): ImageData | undefined {
  return imageCache.get(id);
}

export function listImageIds(): string[] {
  return [...imageCache.keys()];
}

export const imageSpec: LayerSpec = {
  kind: 'image',
  category: 'generator',
  animated: false,
  label: 'Image',
  defaults: { src: 'none', fit: 'contain' } satisfies ImageParams,
  schema: {
    src: { type: 'select', label: 'Source', options: ['none'], default: 'none' },
    fit: { type: 'select', label: 'Fit',    options: ['cover', 'contain', 'stretch'], default: 'contain' },
  },
  run: (_input, params, ctx) => {
    const p = params as ImageParams;
    const w = ctx.width, h = ctx.height;
    const out = new ImageData(w, h);
    const src = imageCache.get(p.src);
    if (!src) return out;
    return blit(src, out, p.fit, w, h);
  },
};

function blit(
  src: ImageData,
  dst: ImageData,
  fit: 'cover' | 'contain' | 'stretch',
  w: number,
  h: number,
): ImageData {
  const sw = src.width, sh = src.height;
  let scaleX = w / sw, scaleY = h / sh;
  if (fit === 'cover') scaleX = scaleY = Math.max(scaleX, scaleY);
  else if (fit === 'contain') scaleX = scaleY = Math.min(scaleX, scaleY);
  const drawW = sw * scaleX, drawH = sh * scaleY;
  const offX = Math.floor((w - drawW) / 2);
  const offY = Math.floor((h - drawH) / 2);

  const sd = src.data;
  const dd = dst.data;
  for (let y = 0; y < h; y++) {
    const sy = Math.floor((y - offY) / scaleY);
    if (sy < 0 || sy >= sh) continue;
    for (let x = 0; x < w; x++) {
      const sx = Math.floor((x - offX) / scaleX);
      if (sx < 0 || sx >= sw) continue;
      const si = (sy * sw + sx) * 4;
      const di = (y * w + x) * 4;
      dd[di]     = sd[si]     as number;
      dd[di + 1] = sd[si + 1] as number;
      dd[di + 2] = sd[si + 2] as number;
      dd[di + 3] = sd[si + 3] as number;
    }
  }
  return dst;
}
